import type { SyntheticEvent } from "react";
import { memo, useCallback, useMemo } from "react";

import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import type { SxProps, Theme } from "@mui/material";
import { Box, IconButton } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import type { ToggleButtonGroupProps } from "@mui/material/ToggleButtonGroup";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useTranslation } from "react-i18next";

import type { MetricInputProps } from "./DataEntryInput.types";
import { InputMode } from "./DataEntryInput.types";
import { loadingYesNoInputSx } from "./loadingInputSx";
import { dirtyBorder, useInputSx } from "./useInputSx";

enum ToggleValuesEnum {
  True = "true",
  False = "false",
}

export type YesNoInputProps = MetricInputProps & {
  isLoading: boolean;
};

const YesNoInput = ({ id, value, mode, onChange, isDirty, isLoading }: YesNoInputProps) => {
  const inputSx = useInputSx({ mode, isDirty, isYesNoInput: true });
  const { t } = useTranslation(["common"]);
  const inputDisabled = mode === InputMode.ReadOnly || mode === InputMode.SiteEdit || isLoading;
  const inputReadOnly = mode === InputMode.Loading;

  const handleClear = useCallback(() => {
    if (inputReadOnly) return;
    onChange("");
  }, [inputReadOnly, onChange]);

  const handleChange: ToggleButtonGroupProps["onChange"] = useCallback(
    (_event: SyntheticEvent, newValue: string) => {
      if (inputReadOnly) return;
      if (newValue === null) return handleClear();
      onChange(newValue);
    },
    [handleClear, inputReadOnly, onChange],
  );

  const validToggleValue = useMemo(() => {
    if (value === ToggleValuesEnum.True || value === ToggleValuesEnum.False) {
      return value;
    }
    return null;
  }, [value]);

  /**
   * Dirty border hack: ToggleButtonGroup doesn't support customizing divider border colors.
   */
  const { yesButtonSx, noButtonSx } = useMemo(() => {
    if (!isDirty) {
      return { yesButtonSx: undefined, noButtonSx: undefined };
    }

    const standardSx = {
      ...inputSx,
      borderTop: dirtyBorder,
      borderBottom: dirtyBorder,
    };

    const yesButtonSx: SxProps<Theme> = { ...standardSx, borderLeft: dirtyBorder };
    const noButtonSx: SxProps<Theme> = { ...standardSx, borderRight: dirtyBorder };

    return { yesButtonSx, noButtonSx } as const;
  }, [inputSx, isDirty]);

  return (
    <Box width="100%" display="flex" alignItems="center">
      <ToggleButtonGroup
        data-component="MetricInput"
        data-id={id}
        value={validToggleValue}
        color="secondary"
        disabled={inputDisabled}
        exclusive
        fullWidth
        onChange={handleChange}
        aria-label={t("common:yes_no")}
        sx={isLoading ? loadingYesNoInputSx : undefined}
      >
        <ToggleButton sx={yesButtonSx} value={ToggleValuesEnum.True} aria-label={t("common:yes")}>
          {t("common:yes")}
        </ToggleButton>
        <ToggleButton sx={noButtonSx} value={ToggleValuesEnum.False} aria-label={t("common:no")}>
          {t("common:no")}
        </ToggleButton>
      </ToggleButtonGroup>
      <IconButton disabled={inputDisabled} onClick={handleClear} aria-label={t("common:clear")}>
        <HighlightOffIcon />
      </IconButton>
    </Box>
  );
};

export default memo(YesNoInput);
